import { getBackend as getBackend2 } from "./backend/backend2";
import { createAkaUrl } from "./aka";
import type {
    IExportModelEvaluationKpiModelsReq,
    IGetLatestModelEvaluationKpiAggregationsRes,
    IGetModelEvaluationKpiModelsReq,
    IGetModelEvaluationKpiModelsRes,
    IModelEvaluationAggregate,
    IModelEvaluationKpiAggregationsReport,
    IModelEvaluationKpiModelRow,
} from "./backend/reports.generated.types";
import type { ILineData, ILineGroupData, IPlantData } from "./backend/manufacturing2.generated.types";
import { isInRoles, ROLE_ADMIN, ROLE_DEMO_USER, ROLE_MAN_DT_VIEWER, ROLE_POWER_USER } from "./auth";

export const MODEL_EVALUATION_KPI_WINDOW_KEYS = ["7d", "30d", "100d"] as const;

export type ModelEvaluationWindowKey = (typeof MODEL_EVALUATION_KPI_WINDOW_KEYS)[number];

/** Backend report storage keys (see gui-server model-evaluation/query.ts). */
const MODEL_EVALUATION_KPI_REPORT_WINDOW_KEYS: Record<ModelEvaluationWindowKey, string> = {
    "7d": "-7days",
    "30d": "-30days",
    "100d": "-100days",
};

const MODEL_EVALUATION_KPI_UI_WINDOW_KEYS: Record<string, ModelEvaluationWindowKey> = {
    "7d": "7d",
    "30d": "30d",
    "100d": "100d",
    "-7days": "7d",
    "-30days": "30d",
    "-100days": "100d",
};

export type ShellViewModel = {
    createdAt: string | null;
    windowKeys: ModelEvaluationWindowKey[];
};

export type ModelEvaluationScopeType = "organization" | "plant" | "line_group" | "line";

export type ModelEvaluationScope = {
    type: ModelEvaluationScopeType;
    plant_id: number | null;
    line_group_id: number | null;
    line_id: number | null;
};

export type ModelEvaluationModelsFilters = {
    plant_id: number | null;
    line_id: number | null;
};

export type EntityLabelMaps = {
    plantsById: Map<number, string>;
    lineGroupsById: Map<number, string>;
    linesById: Map<number, string>;
};

export type SubordinateDrillTarget = "plant" | "line_group" | "line";

export type SubordinateRow = {
    id: number;
    label: string;
    drill: SubordinateDrillTarget;
    plant_id: number;
    line_group_id: number | null;
    line_id: number | null;
    aggregate: IModelEvaluationAggregate;
};

export type SubordinateSortKey =
    | "label"
    | "operations_count"
    | "total_duration_hours"
    | "model_win_count"
    | "normative_win_count"
    | "fallback_count"
    | "avg_model_error_ratio_pct"
    | "avg_normative_error_ratio_pct";

export type SubordinateSort = {
    key: SubordinateSortKey;
    dir: 1 | -1;
};

export const DEFAULT_MODEL_EVALUATION_SCOPE: ModelEvaluationScope = {
    type: "organization",
    plant_id: null,
    line_group_id: null,
    line_id: null,
};

export const DEFAULT_MODEL_EVALUATION_MODELS_FILTERS: ModelEvaluationModelsFilters = {
    plant_id: null,
    line_id: null,
};

export type ModelEvaluationModelsSortKey =
    | "operations_count"
    | "model_win_over_norm_ratio_pct"
    | "avg_model_error_ratio_pct"
    | "line_id"
    | "material_id"
    | "process_num";

export type ModelEvaluationModelsSortDir = "asc" | "desc";

export type ModelEvaluationModelsSort = {
    key: ModelEvaluationModelsSortKey;
    dir: ModelEvaluationModelsSortDir;
};

export type ModelEvaluationModelsFiltersState = {
    plant_id: number | null;
    line_id: number | null;
    material_external_id: string | null;
    active_only: boolean;
};

export const DEFAULT_MODEL_EVALUATION_MODELS_TAB_FILTERS: ModelEvaluationModelsFiltersState = {
    plant_id: null,
    line_id: null,
    material_external_id: null,
    active_only: false,
};

export const DEFAULT_MODEL_EVALUATION_MODELS_SORT: ModelEvaluationModelsSort = {
    key: "operations_count",
    dir: "desc",
};

export const DEFAULT_MODEL_EVALUATION_MODELS_PAGE_SIZE = 50;

const MODELS_SORT_ASC_DEFAULT_KEYS = new Set<ModelEvaluationModelsSortKey>(["line_id", "material_id", "process_num"]);

export function defaultModelsSortDirection(sort_key: ModelEvaluationModelsSortKey): ModelEvaluationModelsSortDir {
    if (MODELS_SORT_ASC_DEFAULT_KEYS.has(sort_key)) {
        return "asc";
    }
    return "desc";
}

export function toggleModelsSort(
    current: ModelEvaluationModelsSort,
    clicked_key: ModelEvaluationModelsSortKey
): ModelEvaluationModelsSort {
    if (current.key === clicked_key) {
        return {
            key: clicked_key,
            dir: current.dir === "asc" ? "desc" : "asc",
        };
    }
    return {
        key: clicked_key,
        dir: defaultModelsSortDirection(clicked_key),
    };
}

export function mergeHandoffIntoModelsFilters(
    current: ModelEvaluationModelsFiltersState,
    handoff: ModelEvaluationModelsFilters
): ModelEvaluationModelsFiltersState {
    return {
        ...current,
        plant_id: handoff.plant_id,
        line_id: handoff.line_id,
    };
}

export function modelsFiltersAreDefault(filters: ModelEvaluationModelsFiltersState): boolean {
    return (
        filters.plant_id == null &&
        filters.line_id == null &&
        filters.material_external_id == null &&
        filters.active_only === DEFAULT_MODEL_EVALUATION_MODELS_TAB_FILTERS.active_only
    );
}

export function formatMaterialLabel(row: { material_id: number; material_external_id?: string }): string {
    if (row.material_external_id != null && row.material_external_id !== "") {
        return row.material_external_id;
    }
    return `Material ${row.material_id}`;
}

export function buildModelBrowserUrl(
    row: Pick<IModelEvaluationKpiModelRow, "line_external_id" | "material_external_id" | "process_num">
): string | null {
    if (!row.line_external_id || !row.material_external_id) {
        return null;
    }
    return createAkaUrl({
        type: "man_model",
        line: row.line_external_id,
        material: row.material_external_id,
        process_num: row.process_num,
    });
}

export function resolveModelBrowserExternalIds(
    row: Pick<IModelEvaluationKpiModelRow, "line_id" | "line_external_id" | "material_external_id" | "process_num">,
    line_external_id_by_line_id?: Map<number, string>
): Pick<IModelEvaluationKpiModelRow, "line_external_id" | "material_external_id" | "process_num"> {
    let line_external_id = row.line_external_id ?? "";
    if (line_external_id === "" && line_external_id_by_line_id != null) {
        line_external_id = line_external_id_by_line_id.get(row.line_id) ?? "";
    }
    return {
        line_external_id,
        material_external_id: row.material_external_id ?? "",
        process_num: row.process_num,
    };
}

export function buildModelsQueryRequest(
    window: ModelEvaluationWindowKey,
    filters: ModelEvaluationModelsFiltersState,
    sort: ModelEvaluationModelsSort,
    page: number,
    page_size: number
): IGetModelEvaluationKpiModelsReq {
    const req: IGetModelEvaluationKpiModelsReq = {
        window,
        page,
        page_size,
        sort: sort.key,
        sort_dir: sort.dir,
    };
    if (filters.plant_id != null) {
        req.plant_id = filters.plant_id;
    }
    if (filters.line_id != null) {
        req.line_id = filters.line_id;
    }
    if (filters.material_external_id != null && filters.material_external_id !== "") {
        req.material_external_id = filters.material_external_id;
    }
    if (filters.active_only) {
        req.active_only = true;
    }
    return req;
}

export function buildModelsExportRequest(
    window: ModelEvaluationWindowKey,
    filters: ModelEvaluationModelsFiltersState,
    sort: ModelEvaluationModelsSort
): IExportModelEvaluationKpiModelsReq {
    const req: IExportModelEvaluationKpiModelsReq = {
        window,
        sort: sort.key,
        sort_dir: sort.dir,
    };
    if (filters.plant_id != null) {
        req.plant_id = filters.plant_id;
    }
    if (filters.line_id != null) {
        req.line_id = filters.line_id;
    }
    if (filters.material_external_id != null && filters.material_external_id !== "") {
        req.material_external_id = filters.material_external_id;
    }
    if (filters.active_only) {
        req.active_only = true;
    }
    return req;
}

export function clampModelsPageIndex(page_index: number, total_rows: number, page_size: number): number {
    if (total_rows <= 0) {
        return 0;
    }
    const page_count = Math.ceil(total_rows / page_size);
    return Math.min(Math.max(0, page_index), page_count - 1);
}

export function canAccessModelEvaluationKpi(): boolean {
    return isInRoles([ROLE_ADMIN, ROLE_POWER_USER, ROLE_MAN_DT_VIEWER, ROLE_DEMO_USER]);
}

export function isModelEvaluationReportNotFound(err: unknown): boolean {
    if (!(err instanceof Error)) {
        return false;
    }
    return err.message.toLowerCase().includes("not found");
}

export function modelEvaluationJobUuidMismatch(
    aggregations_job_uuid: string | undefined,
    models_job_uuid: string | undefined
): boolean {
    if (aggregations_job_uuid == null || aggregations_job_uuid === "") {
        return false;
    }
    if (models_job_uuid == null || models_job_uuid === "") {
        return false;
    }
    return aggregations_job_uuid !== models_job_uuid;
}

export async function fetchLatestAggregations(): Promise<IGetLatestModelEvaluationKpiAggregationsRes> {
    return getBackend2().reports.getLatestModelEvaluationKpiAggregations({});
}

export async function fetchModelsPage(req: IGetModelEvaluationKpiModelsReq): Promise<IGetModelEvaluationKpiModelsRes> {
    return getBackend2().reports.getModelEvaluationKpiModels(req);
}

export async function exportModelsCsv(req: IExportModelEvaluationKpiModelsReq): Promise<string[][]> {
    const res = await getBackend2().reports.exportModelEvaluationKpiModels(req);
    return res.csv;
}

export function rangeCaptionForWindow(
    aggregations: IModelEvaluationKpiAggregationsReport,
    window: ModelEvaluationWindowKey
): string | null {
    const aggregate = aggregateForWindow(aggregations.org_aggregates, window);
    if (aggregate == null) {
        return null;
    }
    return `${aggregate.start_date} → ${aggregate.end_date}`;
}

function findPlant(report: IModelEvaluationKpiAggregationsReport, plant_id: number) {
    return report.plants.find(plant => plant.id === plant_id) ?? null;
}

function findLineGroup(report: IModelEvaluationKpiAggregationsReport, plant_id: number, line_group_id: number) {
    const plant = findPlant(report, plant_id);
    if (plant == null) {
        return null;
    }
    return plant.line_groups.find(line_group => line_group.id === line_group_id) ?? null;
}

function findLine(
    report: IModelEvaluationKpiAggregationsReport,
    plant_id: number,
    line_group_id: number,
    line_id: number
) {
    const line_group = findLineGroup(report, plant_id, line_group_id);
    if (line_group == null) {
        return null;
    }
    return line_group.lines.find(line => line.id === line_id) ?? null;
}

function resolveAggregateMapKey(
    aggregate_map: Record<string, IModelEvaluationAggregate> | undefined,
    window: ModelEvaluationWindowKey
): string | null {
    if (aggregate_map == null) {
        return null;
    }
    if (aggregate_map[window] != null) {
        return window;
    }
    const report_key = MODEL_EVALUATION_KPI_REPORT_WINDOW_KEYS[window];
    if (report_key != null && aggregate_map[report_key] != null) {
        return report_key;
    }
    return null;
}

function aggregateForWindow(
    aggregate_map: Record<string, IModelEvaluationAggregate> | undefined,
    window: ModelEvaluationWindowKey
): IModelEvaluationAggregate | null {
    const map_key = resolveAggregateMapKey(aggregate_map, window);
    if (map_key == null || aggregate_map == null) {
        return null;
    }
    return aggregate_map[map_key] ?? null;
}

export function getScopeAggregate(
    report: IModelEvaluationKpiAggregationsReport,
    scope: ModelEvaluationScope,
    window: ModelEvaluationWindowKey
): IModelEvaluationAggregate | null {
    if (scope.type === "organization") {
        return aggregateForWindow(report.org_aggregates, window);
    }
    if (scope.plant_id == null) {
        return null;
    }
    if (scope.type === "plant") {
        const plant = findPlant(report, scope.plant_id);
        return plant == null ? null : aggregateForWindow(plant.aggregates, window);
    }
    if (scope.line_group_id == null) {
        return null;
    }
    if (scope.type === "line_group") {
        const line_group = findLineGroup(report, scope.plant_id, scope.line_group_id);
        return line_group == null ? null : aggregateForWindow(line_group.aggregates, window);
    }
    if (scope.line_id == null) {
        return null;
    }
    const line = findLine(report, scope.plant_id, scope.line_group_id, scope.line_id);
    return line == null ? null : aggregateForWindow(line.aggregates, window);
}

export function getSubordinateRows(
    report: IModelEvaluationKpiAggregationsReport,
    scope: ModelEvaluationScope,
    window: ModelEvaluationWindowKey,
    label_maps: EntityLabelMaps
): SubordinateRow[] {
    const rows: SubordinateRow[] = [];

    if (scope.type === "organization") {
        for (const plant of report.plants) {
            const aggregate = aggregateForWindow(plant.aggregates, window);
            if (aggregate == null) {
                continue;
            }
            rows.push({
                id: plant.id,
                label: label_maps.plantsById.get(plant.id) ?? `Plant ${plant.id}`,
                drill: "plant",
                plant_id: plant.id,
                line_group_id: null,
                line_id: null,
                aggregate,
            });
        }
        return rows;
    }
    if (scope.plant_id == null) {
        return rows;
    }
    const plant = findPlant(report, scope.plant_id);
    if (plant == null) {
        return rows;
    }
    if (scope.type === "plant") {
        for (const line_group of plant.line_groups) {
            const aggregate = aggregateForWindow(line_group.aggregates, window);
            if (aggregate == null) {
                continue;
            }
            rows.push({
                id: line_group.id,
                label: label_maps.lineGroupsById.get(line_group.id) ?? `Line group ${line_group.id}`,
                drill: "line_group",
                plant_id: plant.id,
                line_group_id: line_group.id,
                line_id: null,
                aggregate,
            });
        }
        return rows;
    }
    if (scope.line_group_id == null) {
        return rows;
    }
    const line_group = findLineGroup(report, scope.plant_id, scope.line_group_id);
    if (line_group == null) {
        return rows;
    }
    if (scope.type === "line_group") {
        for (const line of line_group.lines) {
            const aggregate = aggregateForWindow(line.aggregates, window);
            if (aggregate == null) {
                continue;
            }
            rows.push({
                id: line.id,
                label: label_maps.linesById.get(line.id) ?? `Line ${line.id}`,
                drill: "line",
                plant_id: plant.id,
                line_group_id: line_group.id,
                line_id: line.id,
                aggregate,
            });
        }
    }
    return rows;
}

export function buildEntityLabelMaps(
    report: IModelEvaluationKpiAggregationsReport,
    plants: IPlantData[],
    line_groups: ILineGroupData[],
    lines: ILineData[]
): EntityLabelMaps {
    const plants_by_uuid = new Map(plants.map(plant => [plant.uuid, plant.title]));
    const line_groups_by_id = new Map(line_groups.map(line_group => [line_group.id, line_group]));
    const lines_by_id = new Map(lines.map(line => [line.id, line]));

    const plants_by_id = new Map<number, string>();
    for (const plant of report.plants) {
        let title: string | null = null;
        for (const line_group of plant.line_groups) {
            const line_group_data = line_groups_by_id.get(line_group.id);
            if (line_group_data != null && line_group_data.plant_uuids.length > 0) {
                const plant_title = plants_by_uuid.get(line_group_data.plant_uuids[0]);
                if (plant_title != null) {
                    title = plant_title;
                    break;
                }
            }
        }
        if (title == null) {
            for (const line_group of plant.line_groups) {
                for (const line of line_group.lines) {
                    const line_data = lines_by_id.get(line.id);
                    if (line_data != null) {
                        title = line_data.plant_title;
                        break;
                    }
                }
                if (title != null) {
                    break;
                }
            }
        }
        plants_by_id.set(plant.id, title ?? `Plant ${plant.id}`);
    }

    const line_groups_by_id_labels = new Map<number, string>();
    for (const plant of report.plants) {
        for (const line_group of plant.line_groups) {
            const group_title = line_groups_by_id.get(line_group.id)?.title ?? `Line group ${line_group.id}`;
            line_groups_by_id_labels.set(line_group.id, group_title);
        }
    }

    const lines_by_id_labels = new Map<number, string>();
    for (const plant of report.plants) {
        for (const line_group of plant.line_groups) {
            for (const line of line_group.lines) {
                const line_title = lines_by_id.get(line.id)?.title ?? `Line ${line.id}`;
                lines_by_id_labels.set(line.id, line_title);
            }
        }
    }

    return {
        plantsById: plants_by_id,
        lineGroupsById: line_groups_by_id_labels,
        linesById: lines_by_id_labels,
    };
}

export function normalizeScope(
    report: IModelEvaluationKpiAggregationsReport,
    scope: ModelEvaluationScope
): ModelEvaluationScope {
    if (scope.type === "organization") {
        return { ...DEFAULT_MODEL_EVALUATION_SCOPE };
    }
    if (report.plants.length === 0) {
        return { ...DEFAULT_MODEL_EVALUATION_SCOPE };
    }

    let plant_id = scope.plant_id;
    if (plant_id == null || findPlant(report, plant_id) == null) {
        plant_id = report.plants[0].id;
    }

    if (scope.type === "plant") {
        return {
            type: "plant",
            plant_id,
            line_group_id: null,
            line_id: null,
        };
    }

    const plant = findPlant(report, plant_id);
    if (plant == null || plant.line_groups.length === 0) {
        return {
            type: "plant",
            plant_id,
            line_group_id: null,
            line_id: null,
        };
    }

    let line_group_id = scope.line_group_id;
    if (line_group_id == null || findLineGroup(report, plant_id, line_group_id) == null) {
        line_group_id = plant.line_groups[0].id;
    }

    if (scope.type === "line_group") {
        return {
            type: "line_group",
            plant_id,
            line_group_id,
            line_id: null,
        };
    }

    const line_group = findLineGroup(report, plant_id, line_group_id);
    if (line_group == null || line_group.lines.length === 0) {
        return {
            type: "line_group",
            plant_id,
            line_group_id,
            line_id: null,
        };
    }

    let line_id = scope.line_id;
    if (line_id == null || findLine(report, plant_id, line_group_id, line_id) == null) {
        line_id = line_group.lines[0].id;
    }

    return {
        type: "line",
        plant_id,
        line_group_id,
        line_id,
    };
}

export function scopeFromDrillRow(row: SubordinateRow): ModelEvaluationScope {
    if (row.drill === "plant") {
        return {
            type: "plant",
            plant_id: row.plant_id,
            line_group_id: null,
            line_id: null,
        };
    }
    if (row.drill === "line_group") {
        return {
            type: "line_group",
            plant_id: row.plant_id,
            line_group_id: row.line_group_id,
            line_id: null,
        };
    }
    return {
        type: "line",
        plant_id: row.plant_id,
        line_group_id: row.line_group_id,
        line_id: row.line_id,
    };
}

export function defaultSortForScope(scope_type: ModelEvaluationScopeType): SubordinateSort {
    if (scope_type === "line_group") {
        return { key: "label", dir: 1 };
    }
    return { key: "operations_count", dir: -1 };
}

export function sortSubordinateRows(rows: SubordinateRow[], sort: SubordinateSort): SubordinateRow[] {
    const sorted = [...rows];
    sorted.sort((left, right) => {
        let left_value: string | number;
        let right_value: string | number;
        if (sort.key === "label") {
            left_value = left.label.toLowerCase();
            right_value = right.label.toLowerCase();
        } else {
            left_value = left.aggregate[sort.key];
            right_value = right.aggregate[sort.key];
        }
        if (left_value < right_value) {
            return -1 * sort.dir;
        }
        if (left_value > right_value) {
            return 1 * sort.dir;
        }
        return 0;
    });
    return sorted;
}

export function formatDurationHours(hours: number): string {
    const h = Number(hours);
    if (h === 0 || (h !== 0 && !h)) {
        return "0";
    }
    return (Math.round(h * 10) / 10).toLocaleString();
}

export function formatErrorRatioPct(value: number): string {
    const n = Number(value);
    if (n === 0 || (n !== 0 && !n)) {
        return "0.00%";
    }
    return `${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

export function formatCountWithPct(count: number, operations_count: number): string {
    const ops = operations_count || 0;
    const pct = ops ? Math.round((count / ops) * 1000) / 10 : 0;
    return `${count.toLocaleString()} (${pct}%)`;
}

export function winMixBarPercentages(
    model_wins: number,
    norm_wins: number,
    fallback_count: number
): { model_pct: number; norm_pct: number; fallback_pct: number; total: number } {
    const m = Math.max(0, Math.floor(Number(model_wins) || 0));
    const n = Math.max(0, Math.floor(Number(norm_wins) || 0));
    const f = Math.max(0, Math.floor(Number(fallback_count) || 0));
    const total = m + n + f;
    if (total <= 0) {
        return { model_pct: 0, norm_pct: 0, fallback_pct: 0, total: 0 };
    }
    const model_pct = (m / total) * 100;
    const norm_pct = ((m + n) / total) * 100;
    return {
        model_pct,
        norm_pct,
        fallback_pct: 100,
        total,
    };
}

export function mapShellViewModel(aggregations: IGetLatestModelEvaluationKpiAggregationsRes): ShellViewModel {
    const reported_keys = aggregations.metadata.time_window_keys ?? [];
    const window_keys = reported_keys
        .map(key => MODEL_EVALUATION_KPI_UI_WINDOW_KEYS[key])
        .filter((key): key is ModelEvaluationWindowKey => key != null);

    return {
        createdAt: aggregations.metadata.created_at ?? null,
        windowKeys: window_keys.length > 0 ? window_keys : [...MODEL_EVALUATION_KPI_WINDOW_KEYS],
    };
}
